import React, { useState, useEffect } from 'react';
import { DynamicIcon } from '../icons/DynamicIcon';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface Drink {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  is_standard: boolean;
}

interface SelectedDrink {
  id: string;
  name: string;
  quantity: number;
  is_standard: boolean;
}

interface TabSelecaoDrinksProps {
  eventoData: {
    drinksEscolhidos: SelectedDrink[];
  };
  setEventoData: React.Dispatch<React.SetStateAction<any>>;
}

const TabSelecaoDrinks: React.FC<TabSelecaoDrinksProps> = ({ eventoData, setEventoData }) => {
  const [drinks, setDrinks] = useState<Drink[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDrinkSelector, setShowDrinkSelector] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    fetchDrinks();
  }, []);

  const fetchDrinks = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('drinks')
        .select('*')
        .order('name');

      if (error) throw error;
      
      setDrinks(data || []);
      
      // Extract unique categories
      const uniqueCategories = Array.from(
        new Set(data?.map(drink => drink.category).filter(Boolean) as string[])
      );
      setCategories(uniqueCategories);
    } catch (err: any) {
      console.error('Error fetching drinks:', err);
      toast.error('Erro ao carregar drinks');
    } finally {
      setLoading(false);
    }
  };

  const addDrink = (drink: Drink) => {
    // Check if drink is already selected
    const existingIndex = eventoData.drinksEscolhidos.findIndex(d => d.id === drink.id);
    
    if (existingIndex >= 0) {
      // Increment quantity if already selected
      const updatedDrinks = [...eventoData.drinksEscolhidos];
      updatedDrinks[existingIndex].quantity += 1;
      setEventoData({
        ...eventoData,
        drinksEscolhidos: updatedDrinks
      });
    } else {
      // Add new drink
      setEventoData({
        ...eventoData,
        drinksEscolhidos: [
          ...eventoData.drinksEscolhidos,
          {
            id: drink.id,
            name: drink.name,
            quantity: 1,
            is_standard: drink.is_standard
          }
        ]
      });
    }
    
    toast.success(`${drink.name} adicionado`);
  };

  const removeDrink = (drinkId: string) => {
    setEventoData({
      ...eventoData,
      drinksEscolhidos: eventoData.drinksEscolhidos.filter(d => d.id !== drinkId)
    });
  };

  const updateDrinkQuantity = (drinkId: string, quantity: number) => {
    if (quantity < 1) return;
    
    setEventoData({
      ...eventoData,
      drinksEscolhidos: eventoData.drinksEscolhidos.map(d => 
        d.id === drinkId ? { ...d, quantity } : d
      )
    });
  };

  const toggleDrinkStandard = (drinkId: string) => {
    setEventoData({
      ...eventoData,
      drinksEscolhidos: eventoData.drinksEscolhidos.map(d => 
        d.id === drinkId ? { ...d, is_standard: !d.is_standard } : d
      )
    });
  };

  const filteredDrinks = drinks.filter(drink => {
    const matchesSearch = searchTerm === '' || 
      drink.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (drink.description && drink.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === null || drink.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-bold mb-6 flex items-center">
        <DynamicIcon name="glasses" size={24} className="mr-2 text-blue-600" />
        Seleção de Drinks
      </h2>
      
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Drinks Selecionados ({eventoData.drinksEscolhidos.length})
          </h3>
          <button
            onClick={() => setShowDrinkSelector(true)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <DynamicIcon name="plus" size={16} className="mr-1" />
            Adicionar Drink
          </button>
        </div>

        {eventoData.drinksEscolhidos.length === 0 ? (
          <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
            <DynamicIcon name="glasses" size={48} className="mx-auto text-gray-300 mb-2" />
            <p>Nenhum drink selecionado</p>
            <p className="text-sm">Clique em "Adicionar Drink" para começar</p>
          </div>
        ) : (
          <div className="space-y-3">
            {eventoData.drinksEscolhidos.map((drink) => (
              <div key={drink.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="font-medium">{drink.name}</span>
                    <span 
                      className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                        drink.is_standard 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {drink.is_standard ? 'Standard' : 'Premium'}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => toggleDrinkStandard(drink.id)}
                    className="p-1 text-gray-400 hover:text-blue-600"
                    title={drink.is_standard ? "Mudar para Premium" : "Mudar para Standard"}
                  >
                    <DynamicIcon name={drink.is_standard ? "star" : "check"} size={16} />
                  </button>
                  
                  <div className="flex items-center border rounded-md">
                    <button
                      onClick={() => updateDrinkQuantity(drink.id, drink.quantity - 1)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                      disabled={drink.quantity <= 1}
                    >
                      <DynamicIcon name="minus" size={16} />
                    </button>
                    <span className="w-8 text-center">{drink.quantity}</span>
                    <button
                      onClick={() => updateDrinkQuantity(drink.id, drink.quantity + 1)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <DynamicIcon name="plus" size={16} />
                    </button>
                  </div>
                  
                  <button
                    onClick={() => removeDrink(drink.id)}
                    className="p-1 text-red-400 hover:text-red-600"
                    title="Remover"
                  >
                    <DynamicIcon name="trash" size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Drink Selector Modal */}
      {showDrinkSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-medium">Selecionar Drinks</h3>
              <button
                onClick={() => setShowDrinkSelector(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <DynamicIcon name="x" size={20} />
              </button>
            </div>
            
            <div className="p-6">
              {/* Search and Filter */}
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DynamicIcon name="search" size={20} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar drinks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <select
                  value={selectedCategory || ''}
                  onChange={(e) => setSelectedCategory(e.target.value || null)}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todas as categorias</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              
              {/* Drinks Grid */}
              <div className="overflow-y-auto max-h-[60vh]">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : filteredDrinks.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <DynamicIcon name="search" size={48} className="mx-auto text-gray-300 mb-2" />
                    <p>Nenhum drink encontrado</p>
                    <p className="text-sm">Tente ajustar sua busca</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredDrinks.map(drink => (
                      <div
                        key={drink.id}
                        className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors"
                        onClick={() => addDrink(drink)}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium">{drink.name}</h4>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            drink.is_standard 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {drink.is_standard ? 'Standard' : 'Premium'}
                          </span>
                        </div>
                        {drink.description && (
                          <p className="text-sm text-gray-600 line-clamp-2">{drink.description}</p>
                        )}
                        {drink.category && (
                          <p className="text-xs text-blue-600 mt-2">{drink.category}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-between items-center p-4 border-t bg-gray-50">
              <div className="text-sm text-gray-500">
                {filteredDrinks.length} drinks encontrados
              </div>
              <button
                onClick={() => setShowDrinkSelector(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Concluir Seleção
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabSelecaoDrinks;